/**
 * Utilisation Report — full page, booking-driven.
 * Access Evo Design System compliant.
 *
 * "Utilised"  = confirmed / completed bookings.
 * "Planned"   = planned (unconfirmed) bookings.
 * No billable/non-billable split — billing is in the external PSA.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';
import {
  addDays,
  format,
  startOfQuarter,
  endOfQuarter,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';
import {
  AlertTriangle,
  BarChart2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Download,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Loader2,
  SlidersHorizontal,
  Settings2,
  RotateCcw,
  GripVertical,
  X,
} from 'lucide-react';


import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetHeader, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Check, Filter } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useScheduler, getDefaultStatusColor } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useReportScope } from '@/hooks/useReportScope';
import { useScrollbarGutter } from '@/hooks/useScrollbarGutter';
import { useCompany } from '@/contexts/CompanyContext';
import { bookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { usePermissions } from '@/contexts/PermissionsContext';
import {
  useUtilisationData,
  type UtilisationRow,
  type UtilisationType,
  type ViewBy,
} from './useUtilisationData';
import type { Booking } from '@/types';
import {
  RAW_TABLE_CONTAINER_CLASS,
  RAW_TABLE_SCROLL_CLASS,
  TABLE_HEADER_ROW_CLASS,
  RAW_TABLE_CLASS,
  TABLE_HOVER_BG,
  alternatingRowBackground,
} from '@/lib/table-styles';

// ─── Colour tokens ────────────────────────────────────────────────────────────
// Resolved once at module level from EDS CSS tokens so Recharts (which requires
// plain colour strings) stays in sync with the design system.  All non-chart
// usages below use semantic Tailwind classes / CSS variable inline styles.

function resolveToken(token: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return v || fallback;
}

// These are intentionally evaluated lazily (inside components / render) via the
// helper above when Recharts needs a concrete string.  For inline-style usage
// outside Recharts we prefer `var(--...)` directly.
const TEAL        = () => resolveToken('--teal-500',         'oklch(0.50 0.0896 194.8)');
const AMBER       = () => '#EF9F27';
const RED         = () => resolveToken('--destructive',      'oklch(0.50 0.2103 31.9)');
const PLANNED_BLUE_FALLBACK = () => getDefaultStatusColor('planned');
const GREY_DASHED = () => resolveToken('--muted-foreground', 'oklch(0.50 0.0186 248.7)');

// ─── Sub-components ───────────────────────────────────────────────────────────

// Segmented control
interface SegmentedProps<T extends string> {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}
function Segmented<T extends string>({ options, value, onChange }: SegmentedProps<T>) {
  return (
    <div className="inline-flex items-center gap-1.5 shrink-0">
      {options.map((o) => (
        <Button
          key={o.value}
          type="button"
          variant={o.value === value ? 'default' : 'outline'}
          size="sm"
          className="h-8"
          onClick={() => onChange(o.value)}
          aria-pressed={o.value === value}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
}

// Sortable column header
interface SortHeaderProps {
  label: string;
  col: string;
  sortCol: string | null;
  sortDir: 'asc' | 'desc';
  onSort: (col: string) => void;
  align?: 'left' | 'right';
  minWidth?: number;
}
function SortHeader({ label, col, sortCol, sortDir, onSort, align = 'left', minWidth }: SortHeaderProps) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="cursor-pointer select-none px-3 py-2.5 font-medium text-[12px] text-muted-foreground whitespace-nowrap group"
      style={{ textAlign: align, minWidth }}
    >
      <span
        className="inline-flex items-center gap-1"
        style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUp className="h-3 w-3 text-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 text-foreground" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-40 group-hover:opacity-70 transition-opacity" />
        )}
      </span>
    </th>
  );
}

// Pct cell with colour coding
function PctCell({ pct, targetPct }: { pct: number; targetPct: number }) {
  let colour = 'text-foreground';
  if (pct > 100) colour = 'text-destructive';
  else if (pct >= targetPct) colour = 'text-success-500';
  else if (pct >= targetPct - 10) colour = 'text-warning-500';
  else colour = 'text-destructive';
  return <span className={`${colour} font-medium`}>{pct}%</span>;
}

// Variance cell
function VarianceCell({ variance }: { variance: number }) {
  const colour = variance >= 0 ? 'text-success-500' : 'text-destructive';
  const prefix = variance >= 0 ? '+' : '';
  return <span className={colour}>{prefix}{variance}%</span>;
}

// Status pill
function StatusPill({ status }: { status: UtilisationRow['status'] }) {
  switch (status) {
    case 'on-target':
      return <Badge variant="success" accent="success" className="text-[11px] px-2 py-0.5">On target</Badge>;
    case 'below-target':
      return <Badge variant="warning" accent="warning" className="text-[11px] px-2 py-0.5">Below target</Badge>;
    case 'overloaded':
      return <Badge variant="destructive" accent="destructive" className="text-[11px] px-2 py-0.5">Overloaded</Badge>;
    default:
      return <Badge variant="outline" className="text-[11px] px-2 py-0.5 text-muted-foreground">No bookings</Badge>;
  }
}

// Booking status pill (inside drawer)
function BookingStatusPill({ status }: { status: 'confirmed' | 'planned' }) {
  if (status === 'confirmed') {
    return <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0 capitalize">Confirmed</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground capitalize">Planned</Badge>;
}

// Summary stat card
type StatCardAccent = 'teal' | 'warning' | 'default';

const statCardAccentClasses: Record<StatCardAccent, { value: string }> = {
  teal:    { value: 'text-[var(--accent-teal-900)] dark:text-[var(--accent-teal-500)]' },
  warning: { value: 'text-[var(--accent-warning-900)] dark:text-[var(--accent-warning-500)]' },
  default: { value: 'text-foreground' },
};

interface StatCardProps {
  value: string;
  accent?: StatCardAccent;
  label: string;
  subLabel: string;
  /** When true the card acts as a toggle filter — shows pointer cursor and ring on active */
  isFilterable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}
function StatCard({ value, accent = 'default', label, subLabel, isFilterable, isActive, onClick }: StatCardProps) {
  const classes = statCardAccentClasses[accent];
  return (
    <div
      className={[
        'flex-1 flex flex-col gap-1 rounded-[var(--radius-md)] border bg-card px-4 py-4 min-w-0 transition-all duration-150',
        isFilterable
          ? 'cursor-pointer select-none hover:border-primary/50 hover:shadow-sm'
          : '',
        isActive
          ? 'border-primary ring-2 ring-primary/20 shadow-sm'
          : 'border-border',
      ].join(' ')}
      role={isFilterable ? 'button' : undefined}
      tabIndex={isFilterable ? 0 : undefined}
      aria-pressed={isFilterable ? isActive : undefined}
      onClick={isFilterable ? onClick : undefined}
      onKeyDown={isFilterable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
    >
      <div className="flex items-start justify-between gap-1">
        <span className={`text-2xl font-semibold leading-none ${classes.value}`}>
          {value}
        </span>
        {isFilterable && isActive && (
          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </span>
        )}
        {isFilterable && !isActive && (
          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-muted text-muted-foreground shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1 4H7M4 1L7 4L4 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )}
      </div>
      <span className="text-xs text-muted-foreground mt-1">{label}</span>
      <span className="text-xs text-muted-foreground">{subLabel}</span>
      {isFilterable && !isActive && (
        <span className="text-[10px] text-primary/70 mt-1 font-medium">Click to filter ↓</span>
      )}
      {isFilterable && isActive && (
        <span className="text-[10px] text-primary mt-1 font-medium">Filtering active · click to clear</span>
      )}
    </div>
  );
}

// Chart legend swatch
function LegendSwatch({ colour, label, dashed }: { colour: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      {dashed ? (
        <svg width="16" height="4" className="shrink-0">
          <line x1="0" y1="2" x2="16" y2="2" stroke={colour} strokeWidth="1.5" strokeDasharray="3 2" />
        </svg>
      ) : (
        <span className="inline-block h-3 w-3 rounded-[2px] shrink-0" style={{ background: colour }} />
      )}
      {label}
    </span>
  );
}

// Custom chart tooltip
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { target: number } }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const target = payload[0]?.payload?.target ?? 75;
  const utilised = payload.find((p) => p.name === 'utilised')?.value ?? 0;
  const planned = payload.find((p) => p.name === 'planned')?.value ?? 0;
  const variance = utilised - target;
  return (
    <div
      className="rounded-md shadow-md text-[12px] p-3 flex flex-col gap-1 min-w-[170px]"
      style={{
        background: 'var(--popover)',
        border: '0.5px solid var(--border)',
        color: 'var(--popover-foreground)',
      }}
    >
      <p className="font-semibold mb-1">{label}</p>
      <p>Utilised: <strong>{utilised}%</strong></p>
      <p>Planned: <strong>{planned}%</strong></p>
      <p>Target: <strong>{target}%</strong></p>
      <p className={variance >= 0 ? 'text-success-500' : 'text-destructive'}>
        Variance: <strong>{variance >= 0 ? '+' : ''}{variance}%</strong>
      </p>
    </div>
  );
}

// ─── Colour class resolver for utilisedColour CSS var strings ────────────────
// The hook returns utilisedColour as a CSS var string. We map it to semantic
// Tailwind classes so the drawer never uses raw inline colour styles.
function utilisedColourClass(utilisedColour: string): {
  text: string;
  progressVar: string;
} {
  if (utilisedColour.includes('destructive'))
    return { text: 'text-destructive', progressVar: 'var(--destructive)' };
  if (utilisedColour.includes('warning'))
    return { text: 'text-warning-500', progressVar: 'var(--warning-500)' };
  // default → teal (on-target / no-bookings)
  return { text: 'text-teal-500', progressVar: 'var(--teal-500)' };
}

// ─── Resource detail drawer ───────────────────────────────────────────────────

function ResourceDrawer({
  row,
  open,
  onClose,
  onNavigateToHeatmap,
}: {
  row: UtilisationRow | null;
  open: boolean;
  onClose: () => void;
  onNavigateToHeatmap?: () => void;
}) {
  if (!row) return null;

  const sparkData = row.weeklyUtilisation.map((v, i) => ({ w: i, pct: v }));
  const colourCls = utilisedColourClass(row.utilisedColour);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col gap-0 overflow-hidden w-full sm:max-w-[520px]"
      >
        {/* Visually hidden title — required for screen reader announcement (WCAG 4.1.2) */}
        <SheetTitle className="sr-only">{row.name} — Utilisation Detail</SheetTitle>
        <SheetDescription className="sr-only">
          Utilisation breakdown, bookings and trend data for {row.name}.
        </SheetDescription>

        {/* ── Header ── */}
        <div className="flex items-start justify-between px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-11 w-11 shrink-0">
              <AvatarImage src={row.photoUrl} alt={row.name} />
              <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                {row.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-foreground truncate">{row.name}</p>
              <p className="text-xs text-muted-foreground truncate">{row.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 shrink-0 ml-3">
            <div className="text-right">
              <p className={`text-2xl font-semibold leading-none ${colourCls.text}`}>
                {row.utilisedPct}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Utilised</p>
            </div>
            <StatusPill status={row.status} />
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto">

          {/* ── Burnout risk — shown first when present (highest priority signal) ── */}
          {row.hasBurnoutFlag && (
            <div className="px-5 pt-4 pb-0">
              <Alert variant="warning" size="compact">
                <AlertDescription>
                  <span>
                    <strong>{row.name}</strong> is trending toward{' '}
                    <strong>{row.burnoutPct}%</strong> utilisation
                    {row.burnoutWeek ? ` in ${row.burnoutWeek}` : ''}.
                  </span>
                  {onNavigateToHeatmap && (
                    <button
                      onClick={onNavigateToHeatmap}
                      className="ml-2 inline-flex items-center gap-1 text-xs font-medium underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      View in heatmap
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </button>
                  )}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* ── Section 1 — Utilisation breakdown ── */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Utilisation breakdown</p>

            <div className="flex flex-col gap-3">
              {/* Utilised bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Utilised (confirmed)</span>
                  <span className={`font-semibold ${colourCls.text}`}>{row.utilisedPct}%</span>
                </div>
                <div className="relative">
                  <Progress
                    value={Math.min(row.utilisedPct, 100)}
                    className="h-2"
                    style={{ '--progress-color': colourCls.progressVar, background: 'var(--muted)' } as React.CSSProperties}
                  />
                  {/* Target marker */}
                  <div
                    className="absolute top-0 bottom-0 w-[2px] rounded opacity-60 bg-teal-500"
                    style={{ left: `${Math.min(row.targetPct, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Target: {row.targetPct}%
                </p>
              </div>

              {/* Planned bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Planned (confirmed + planned)</span>
                  <span className="font-semibold text-foreground">{row.plannedPct}%</span>
                </div>
                <Progress
                  value={Math.min(row.plannedPct, 100)}
                  className="h-2"
                  style={{ background: 'var(--muted)' } as React.CSSProperties}
                />
              </div>

              {/* Stat chips */}
              <div className="flex gap-2 mt-1">
                {[
                  { label: 'Confirmed', value: `${row.utilisedHours}h` },
                  { label: 'Planned',   value: `${row.plannedHours}h` },
                  { label: 'Available', value: `${row.availableHours}h` },
                ].map((chip) => (
                  <div
                    key={chip.label}
                    className="flex-1 rounded-md px-2.5 py-2 flex flex-col gap-0.5 text-center bg-muted border border-border"
                  >
                    <span className="text-sm font-semibold text-foreground">{chip.value}</span>
                    <span className="text-xs text-muted-foreground">{chip.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Section 2 — Booking breakdown ── */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Booking breakdown</p>
            {row.periodBookings.length === 0 ? (
              <p className="text-xs text-muted-foreground">No bookings in this period.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {row.periodBookings.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-start justify-between gap-2 rounded-md border border-border px-3 py-2.5 cursor-pointer hover:bg-accent/40 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{b.projectName}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {b.startDate} – {b.endDate}
                      </p>
                      <p className="text-xs text-muted-foreground">{b.allocationPct}% allocation</p>
                    </div>
                    <BookingStatusPill status={b.status} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Section 3 — Trend sparkline ── */}
          <div className="px-5 py-4">
            <p className="text-xs font-semibold text-foreground mb-3">Trend — last 12 weeks</p>
            <div className="h-20 w-full overflow-hidden">
              <LineChart
                width={460}
                height={80}
                data={sparkData}
                margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
              >
                <ReferenceLine y={row.targetPct} stroke={TEAL()} strokeDasharray="3 2" strokeWidth={1} />
                <Line
                  type="monotone"
                  dataKey="pct"
                  stroke={TEAL()}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Dashed line = target ({row.targetPct}%)
            </p>
          </div>

        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div
        className="flex items-center justify-center h-14 w-14 rounded-2xl"
        style={{ background: 'var(--muted)' }}
      >
        <BarChart2 className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="text-[15px] font-medium text-foreground">
        No utilisation data for the selected period
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs">
        Try adjusting the filters or date range.
      </p>
      <button
        onClick={onReset}
        className="text-[13px] text-primary hover:underline mt-1 cursor-pointer"
      >
        Reset filters
      </button>
    </div>
  );
}

// ─── Date range display ───────────────────────────────────────────────────────

function formatDateRange(from: Date, to: Date): string {
  return `${format(from, 'dd MMM yyyy')} — ${format(to, 'dd MMM yyyy')}`;
}

// ─── Period → date range derivation ──────────────────────────────────────────

type PeriodKey = 'week' | 'month' | 'quarter' | 'rolling12';

function computeDateRange(p: PeriodKey): { from: Date; to: Date } {
  const today = new Date();
  switch (p) {
    case 'week':
      return {
        from: startOfWeek(today, { weekStartsOn: 1 }),
        to:   endOfWeek(today,   { weekStartsOn: 1 }),
      };
    case 'month':
      return { from: startOfMonth(today), to: endOfMonth(today) };
    case 'quarter':
      return { from: startOfQuarter(today), to: endOfQuarter(today) };
    case 'rolling12':
      return { from: subMonths(today, 12), to: today };
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

export function UtilisationReport({ onNavigateToHeatmap }: { onNavigateToHeatmap?: () => void } = {}) {
  const { canExportReport } = usePermissions();
  const canExport = canExportReport('utilisationReport');

  // ── Data mode (useScreenData auto-switches between mockData and liveData)
  const {
    resources: allResources,
    bookings: contextBookings,
    settings,
    schedulerGroups,
    currentMode,
    isSettingsReady,
  } = useScreenData('calendar');

  // Scope visible resources by the role's report "View Access" (all / team / own)
  const { scopeResources } = useReportScope('utilisationReport');
  const resources = useMemo(() => scopeResources(allResources), [scopeResources, allResources]);

  const { isLoading: isContextLoading } = useScheduler();
  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;

  // ── Defaults — month is the default period, so initialise dates from it
  const defaultPeriod: PeriodKey = 'month';
  const { from: defaultFrom, to: defaultTo } = computeDateRange(defaultPeriod);

  // ── Filter state
  const [dateFrom, setDateFrom] = useState<Date>(defaultFrom);
  const [dateTo, setDateTo] = useState<Date>(defaultTo);
  const [period, setPeriod] = useState<PeriodKey>(defaultPeriod);

  // ── Live bookings (fetched from API for the report date range)
  // liveData.bookings is intentionally empty — bookings aren't bulk-loaded
  // in live mode, so we fetch them ourselves for the report's date range.
  const [liveReportBookings, setLiveReportBookings] = useState<Booking[]>([]);
  const [hasFetchedBookings, setHasFetchedBookings] = useState(false);
  const [liveBookingsError, setLiveBookingsError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);

  const fetchLiveBookings = useCallback(async (start: Date, end: Date) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const id = ++fetchIdRef.current;
    setLiveBookingsError(null);
    try {
      const fetchEnd = addDays(end, 1);
      const apiBookings = await bookingsApi.getByDateRange(
        start.toISOString(),
        fetchEnd.toISOString(),
        false,
        companyIdRef.current,
      );
      if (abortRef.current?.signal.aborted) return;
      if (fetchIdRef.current === id) {
        const mapped = apiBookings.map(mapApiBookingToBooking);
        setLiveReportBookings(mapped);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to fetch live bookings for utilisation report:', message, err);
      if (fetchIdRef.current === id) {
        setLiveBookingsError(message);
        setLiveReportBookings([]);
      }
    } finally {
      if (fetchIdRef.current === id) setHasFetchedBookings(true);
    }
  }, []);

  useEffect(() => {
    if (currentMode !== 'live') {
      setHasFetchedBookings(false);
    }
  }, [currentMode]);

  useEffect(() => {
    if (currentMode !== 'live') return;
    if (isCompanyLoading || !currentCompany?.id) return;
    fetchLiveBookings(dateFrom, dateTo);
    return () => { abortRef.current?.abort(); };
  }, [currentMode, dateFrom, dateTo, fetchLiveBookings, currentCompany?.id, isCompanyLoading]);

  const bookings = currentMode === 'live' ? liveReportBookings : contextBookings;

  // True while either resources or bookings are still loading in live mode.
  const isLiveDataLoading = currentMode === 'live' && (isContextLoading || !hasFetchedBookings);

  // ── Period change: update both the period label AND the date range together
  const handlePeriodChange = useCallback((p: PeriodKey) => {
    const { from, to } = computeDateRange(p);
    setPeriod(p);
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
  }, []);
  const [viewBy, setViewBy] = useState<ViewBy>('individual');
  const [utilType, setUtilType] = useState<UtilisationType>('utilised');
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Staged filter values — only committed to the applied state on "Apply Filters"
  const [stagedResources, setStagedResources] = useState<string[]>([]);
  const [stagedDepartments, setStagedDepartments] = useState<string[]>([]);
  const [stagedTeams, setStagedTeams] = useState<string[]>([]);
  const [stagedRoles, setStagedRoles] = useState<string[]>([]);

  const openFilterDrawer = useCallback(() => {
    setStagedResources(selectedResources);
    setStagedDepartments(selectedDepartments);
    setStagedTeams(selectedTeams);
    setStagedRoles(selectedRoles);
    setFilterDrawerOpen(true);
  }, [selectedResources, selectedDepartments, selectedTeams, selectedRoles]);

  const applyFilters = useCallback(() => {
    setSelectedResources(stagedResources);
    setSelectedDepartments(stagedDepartments);
    setSelectedTeams(stagedTeams);
    setSelectedRoles(stagedRoles);
    setPage(1);
    setFilterDrawerOpen(false);
  }, [stagedResources, stagedDepartments, stagedTeams, stagedRoles]);

  const clearAllFilters = useCallback(() => {
    setSelectedResources([]);
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedRoles([]);
    setStagedResources([]);
    setStagedDepartments([]);
    setStagedTeams([]);
    setStagedRoles([]);
    setPage(1);
    setFilterDrawerOpen(false);
  }, []);
  const [colConfigOpen, setColConfigOpen] = useState(false);

  // Planner Group is only a valid dimension when grouping by scheduler group
  // and groups are configured; otherwise everything is department-based.
  const teamsEnabled = settings.groupingMethod === 'scheduler-group' && schedulerGroups.length > 0;
  // Which grouping filter to render: `null` until company settings and
  // scheduler groups have resolved, so an unhydrated company never falls back
  // to showing Department.
  const groupingFilter: 'team' | 'department' | null = !isSettingsReady
    ? null
    : teamsEnabled
      ? 'team'
      : 'department';
  const groupingColLabel = teamsEnabled ? 'Planner Group' : 'Department';

  // ── Column visibility
  type UtilColKey = 'department' | 'target' | 'utilised' | 'planned' | 'variance' | 'confirmed' | 'plannedh' | 'available' | 'status';
  const ALL_UTIL_COLS: { key: UtilColKey; label: string; description: string; locked?: boolean }[] = [
    { key: 'department',  label: groupingColLabel, description: `Resource ${groupingColLabel.toLowerCase()}`, locked: false },
    { key: 'target',      label: 'Target %',      description: 'Target utilisation percentage',        locked: false },
    { key: 'utilised',    label: 'Utilised %',    description: 'Confirmed booking utilisation',        locked: false },
    { key: 'planned',     label: 'Planned %',     description: 'Planned booking utilisation',          locked: false },
    { key: 'variance',    label: 'Variance',      description: 'Difference from target',               locked: false },
    { key: 'confirmed',   label: 'Confirmed hrs', description: 'Total confirmed booking hours',        locked: false },
    { key: 'plannedh',    label: 'Planned hrs',   description: 'Total planned booking hours',          locked: false },
    { key: 'available',   label: 'Available hrs', description: 'Total available capacity hours',       locked: false },
    { key: 'status',      label: 'Status',        description: 'Utilisation status indicator',         locked: false },
  ];
  const [visibleUtilCols, setVisibleUtilCols] = useState<Set<UtilColKey>>(
    new Set(['department','target','utilised','planned','variance','confirmed','plannedh','available','status'])
  );
  const toggleUtilCol = (key: UtilColKey) => setVisibleUtilCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) { if (next.size > 1) next.delete(key); } else next.add(key);
    return next;
  });
  const resetUtilCols = () => setVisibleUtilCols(new Set(['department','target','utilised','planned','variance','confirmed','plannedh','available','status']));

  // The first table column already shows the grouping dimension, so the
  // standalone column is dropped when it would repeat that same dimension.
  const groupingColIsStandaloneDimension = viewBy === (teamsEnabled ? 'team' : 'department');
  const availableUtilCols = ALL_UTIL_COLS.filter(
    (col) => col.key !== 'department' || !groupingColIsStandaloneDimension,
  );
  const visibleColCount = availableUtilCols.filter((col) => visibleUtilCols.has(col.key)).length;
  const showDepartmentCol = visibleUtilCols.has('department') && !groupingColIsStandaloneDimension;

  // ── Sort state
  const [sortCol, setSortCol] = useState<string | null>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ── Pagination
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // ── Drawer
  const [drawerRow, setDrawerRow] = useState<UtilisationRow | null>(null);

  // ── Card filter — 'at-target' shows on-target resources, null = all
  const [activeCardFilter, setActiveCardFilter] = useState<'at-target' | null>(null);

  // ── Chart controls (independent of table sort/pagination)
  const [chartShowLimit, setChartShowLimit] = useState<number>(25);
  const [chartSortBy, setChartSortBy] = useState<
    'name' | 'utilised-desc' | 'utilised-asc' | 'planned-desc' | 'variance-desc'
  >('utilised-desc');



  // ── Data
  const data = useUtilisationData(dateFrom, dateTo, viewBy, selectedResources, selectedDepartments, selectedTeams, selectedRoles, resources, bookings, settings);



  // ── Resource options for multi-select
  const resourceOptions = useMemo(
    () =>
      resources
        .filter((r) => r.id !== 'unallocated')
        .map((r) => ({ value: r.id, label: r.name })),
    [resources],
  );

  // ── Department options (from r.department)
  const departmentOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const dept = r.department ?? '';
        if (dept && !seen.has(dept)) {
          seen.add(dept);
          acc.push({ value: dept, label: dept });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  // The grouping dimension follows the configured grouping method in both
  // directions, so the view never sits on the dimension that is hidden — a
  // report left on Department stays stale once the method switches back to
  // planner groups.
  useEffect(() => {
    if (!isSettingsReady) return;
    if (viewBy === 'team' && !teamsEnabled) {
      setViewBy('department');
      setSelectedTeams([]);
      setStagedTeams([]);
      setPage(1);
    } else if (viewBy === 'department' && teamsEnabled) {
      setViewBy('team');
      setSelectedDepartments([]);
      setStagedDepartments([]);
      setPage(1);
    }
  }, [viewBy, teamsEnabled, isSettingsReady]);

  // Only the dimension matching the configured grouping method is filterable,
  // so any selection made against the other one is dropped.
  useEffect(() => {
    if (!isSettingsReady) return;
    if (teamsEnabled) {
      setSelectedDepartments([]);
      setStagedDepartments([]);
    } else {
      setSelectedTeams([]);
      setStagedTeams([]);
    }
  }, [teamsEnabled, isSettingsReady]);

  // ── Planner Group filter options
  const teamOptions = useMemo(() => {
    if (!teamsEnabled) return [];
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated' && r.schedulerGroup)
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const team = r.schedulerGroup!;
        if (!seen.has(team)) {
          seen.add(team);
          acc.push({ value: team, label: team });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources, teamsEnabled]);

  // ── Role options
  const roleOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const role = r.role ?? '';
        if (role && !seen.has(role)) {
          seen.add(role);
          acc.push({ value: role, label: role });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  // ── Filtered rows — applied before sort/paginate and chart
  const cardFilteredRows = useMemo(() => {
    if (!activeCardFilter) return data.rows;
    if (activeCardFilter === 'at-target') return data.rows.filter((r) => r.status === 'on-target');
    return data.rows;
  }, [data.rows, activeCardFilter]);

  const cardFilteredChartData = useMemo(() => {
    if (!activeCardFilter) return data.chartData;
    const nameSet = new Set(cardFilteredRows.map((r) => r.name));
    return data.chartData.filter((d) => nameSet.has(d.name));
  }, [data.chartData, cardFilteredRows, activeCardFilter]);

  // ── Chart display data — sorted + sliced independently of the table
  const chartDisplayData = useMemo(() => {
    const sorted = [...cardFilteredChartData].sort((a, b) => {
      switch (chartSortBy) {
        case 'utilised-desc': return b.utilised - a.utilised;
        case 'utilised-asc':  return a.utilised - b.utilised;
        case 'planned-desc':  return b.planned  - a.planned;
        case 'variance-desc': return (b.utilised - b.target) - (a.utilised - a.target);
        case 'name':
        default:              return a.name.localeCompare(b.name);
      }
    });
    return chartShowLimit === -1 ? sorted : sorted.slice(0, chartShowLimit);
  }, [cardFilteredChartData, chartSortBy, chartShowLimit]);



  // ── Sort + paginate rows
  const sortedRows = useMemo(() => {
    if (sortCol === null) return [...cardFilteredRows];
    return [...cardFilteredRows].sort((a, b) => {
      let va: string | number = '';
      let vb: string | number = '';
      switch (sortCol) {
        case 'name':       va = a.name;           vb = b.name;           break;
        case 'department': va = a.department;     vb = b.department;     break;
        case 'target':     va = a.targetPct;      vb = b.targetPct;      break;
        case 'utilised':   va = a.utilisedPct;    vb = b.utilisedPct;    break;
        case 'planned':    va = a.plannedPct;     vb = b.plannedPct;     break;
        case 'variance':   va = a.variancePct;    vb = b.variancePct;    break;
        case 'confirmed':  va = a.utilisedHours;  vb = b.utilisedHours;  break;
        case 'plannedh':   va = a.plannedHours;   vb = b.plannedHours;   break;
        case 'available':  va = a.availableHours; vb = b.availableHours; break;
        default:           va = a.name;           vb = b.name;
      }
      if (typeof va === 'string') {
        return sortDir === 'asc'
          ? va.localeCompare(vb as string)
          : (vb as string).localeCompare(va);
      }
      return sortDir === 'asc'
        ? (va as number) - (vb as number)
        : (vb as number) - (va as number);
    });
  }, [cardFilteredRows, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = useCallback(
    (col: string) => {
      if (col === sortCol) {
        if (sortDir === 'asc') {
          setSortDir('desc');
        } else {
          // third click → reset (no sort)
          setSortCol(null);
          setSortDir('asc');
        }
      } else {
        setSortCol(col);
        setSortDir('asc');
      }
      setPage(1);
    },
    [sortCol, sortDir],
  );

  const handleReset = useCallback(() => {
    const { from, to } = computeDateRange(defaultPeriod);
    setDateFrom(from);
    setDateTo(to);
    setPeriod(defaultPeriod);
    setViewBy('individual');
    setUtilType('utilised');
    setSelectedResources([]);
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedRoles([]);
    setStagedResources([]);
    setStagedDepartments([]);
    setStagedTeams([]);
    setStagedRoles([]);
    setSortCol('name');
    setSortDir('asc');
    setPage(1);
    setChartShowLimit(25);
    setChartSortBy('utilised-desc');
    setActiveCardFilter(null);
  }, [defaultFrom, defaultTo]);

  // ─── Export to Excel ────────────────────────────────────────────────────────
  const handleExportExcel = useCallback(() => {
    if (!data.rows.length) {
      toast.error('Nothing to export', { description: 'No data matches the current filters.' });
      return;
    }

    const dateLabel = `${format(dateFrom, 'dd MMM yyyy')} – ${format(dateTo, 'dd MMM yyyy')}`;

    // Sheet 1 — summary row per resource
    const summaryRows = data.rows.map(r => ({
      'Resource':        r.name,
      'Role':            r.role,
      [groupingColLabel]: r.department,
      'Period':          dateLabel,
      'Target %':        r.targetPct,
      'Utilised %':      r.utilisedPct,
      'Planned %':       r.plannedPct,
      'Variance %':      r.variancePct,
      'Utilised Hours':  r.utilisedHours,
      'Planned Hours':   r.plannedHours,
      'Available Hours': r.availableHours,
      'Status':          r.status.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      'Burnout Risk':    r.hasBurnoutFlag
        ? `Yes — trending ${r.burnoutPct ?? ''}% in ${r.burnoutWeek ?? ''}`
        : 'No',
    }));

    // Sheet 2 — full booking breakdown across all resources
    const bookingRows = data.rows.flatMap(r =>
      r.periodBookings.map(b => ({
        'Resource':     r.name,
        'Project':      b.projectName,
        'Start Date':   b.startDate,
        'End Date':     b.endDate,
        'Allocation %': b.allocationPct,
        'Status':       b.status.charAt(0).toUpperCase() + b.status.slice(1),
      }))
    );

    const wb = XLSX.utils.book_new();

    // Summary sheet — auto-sized columns
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    wsSummary['!cols'] = Object.keys(summaryRows[0]).map(k => ({
      wch: Math.max(k.length, ...summaryRows.map(r => String(r[k as keyof typeof r]).length)) + 2,
    }));
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Utilisation Summary');

    // Bookings sheet — only included when there are bookings
    if (bookingRows.length) {
      const wsBookings = XLSX.utils.json_to_sheet(bookingRows);
      wsBookings['!cols'] = Object.keys(bookingRows[0]).map(k => ({
        wch: Math.max(k.length, ...bookingRows.map(r => String(r[k as keyof typeof r]).length)) + 2,
      }));
      XLSX.utils.book_append_sheet(wb, wsBookings, 'Booking Breakdown');
    }

    const filename = `utilisation-report-${format(dateFrom, 'yyyy-MM-dd')}-to-${format(dateTo, 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success('Export complete', {
      description: `${data.rows.length} resource${data.rows.length !== 1 ? 's' : ''} exported to Excel.`,
    });
  }, [data.rows, dateFrom, dateTo, groupingColLabel]);

  // Footer totals
  const footerTotals = useMemo(() => {
    const rows = data.rows;
    if (rows.length === 0) return null;
    const activeRows = rows.filter((r) => r.utilisedHours > 0 || r.plannedHours > 0);
    return {
      targetAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.targetPct, 0) / activeRows.length)
          : 0,
      utilisedAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.utilisedPct, 0) / activeRows.length)
          : 0,
      plannedAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.plannedPct, 0) / activeRows.length)
          : 0,
      varianceAvg:
        activeRows.length > 0
          ? Math.round(activeRows.reduce((s, r) => s + r.variancePct, 0) / activeRows.length)
          : 0,
      confirmedSum: rows.reduce((s, r) => s + r.utilisedHours, 0),
      plannedSum: rows.reduce((s, r) => s + r.plannedHours, 0),
      availableSum: rows.reduce((s, r) => s + r.availableHours, 0),
    };
  }, [data.rows]);

  // Chart bar colours based on utilType filter
  function barFillUtilised(entry: { utilised: number; target: number }) {
    const base =
      entry.utilised > 100
        ? RED()
        : entry.utilised >= entry.target
        ? TEAL()
        : entry.utilised >= entry.target - 10
        ? AMBER()
        : RED();
    return utilType === 'planned' ? `${base}66` : base; // dim to 40% when filter is "Planned only"
  }
  function barFillPlanned() {
    const pb = settings.statusConfigs?.find(s => String(s.id) === 'planned')?.color || PLANNED_BLUE_FALLBACK();
    return utilType === 'utilised' ? `${pb}66` : pb;
  }

  const { ref: contentRef, gutter: contentGutter } = useScrollbarGutter<HTMLDivElement>();
  const gutterPadding = { paddingRight: `calc(1.5rem + ${contentGutter}px)` };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background" data-tour="reports-suite">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-4 shrink-0"
        style={gutterPadding}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <BarChart2 className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground leading-tight">
              Utilisation report
            </h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Booking-based utilisation — confirmed and planned bookings
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <FeatureFeedbackCollector feature="Utilisation Report" />
          <HelpButton />
        </div>
      </div>

      {/* ── Filters bar ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-6 py-3 shrink-0" style={gutterPadding}>
        {/* Period selector — drives the date range */}
        <Select value={period} onValueChange={(v) => handlePeriodChange(v as PeriodKey)}>
          <SelectTrigger className="h-8 w-auto min-w-[130px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This week</SelectItem>
            <SelectItem value="month">This month</SelectItem>
            <SelectItem value="quarter">This quarter</SelectItem>
            <SelectItem value="rolling12">Rolling 12 months</SelectItem>
          </SelectContent>
        </Select>

        {/* Date range display — read-only */}
        <div
          className="inline-flex items-center h-8 px-3 rounded-md border border-input text-sm text-muted-foreground shrink-0"
        >
          {formatDateRange(dateFrom, dateTo)}
        </div>

        {/* View by */}
        <Select value={viewBy} onValueChange={(v) => { setViewBy(v as ViewBy); setPage(1); setSortCol('name'); setSortDir('asc'); }}>
          <SelectTrigger className="h-8 w-auto min-w-[130px] shrink-0">
            <SelectValue placeholder="View by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual</SelectItem>
            {groupingFilter === 'team' && <SelectItem value="team">Planner Group</SelectItem>}
            {groupingFilter === 'department' && <SelectItem value="department">Department</SelectItem>}
            <SelectItem value="location">Location</SelectItem>
          </SelectContent>
        </Select>

        {/* Utilisation type */}
        <Segmented
          options={[
            { label: 'Utilised', value: 'utilised' },
            { label: 'Planned', value: 'planned' },
            { label: 'Both', value: 'both' },
          ]}
          value={utilType}
          onChange={(v) => setUtilType(v as UtilisationType)}
        />

        <div className="ml-auto flex items-center gap-2">
          {/* Reset — only when secondary filters are active */}
          {(selectedResources.length > 0 || selectedDepartments.length > 0 || selectedTeams.length > 0 || selectedRoles.length > 0) && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              onClick={handleReset}
            >
              <X className="h-4 w-4" />
              Reset
            </Button>
          )}

          {/* Filter button */}
          <Button
            variant={
              selectedResources.length > 0 ||
              selectedDepartments.length > 0 ||
              selectedTeams.length > 0 ||
              selectedRoles.length > 0
                ? 'default'
                : 'outline'
            }
            size="sm"
            className="relative h-8 w-8 p-0 shrink-0"
            onClick={openFilterDrawer}
            aria-label="Open filters"
          >
            <Filter className="h-4 w-4" />
            {(() => {
              const count =
                (selectedResources.length > 0 ? 1 : 0) +
                (selectedDepartments.length > 0 ? 1 : 0) +
                (selectedTeams.length > 0 ? 1 : 0) +
                (selectedRoles.length > 0 ? 1 : 0);
              return count > 0 ? (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                  {count}
                </span>
              ) : null;
            })()}
          </Button>

          {/* Column config button */}
          <Button
            variant="outline"
            size="sm"
            className="relative h-8 w-8 p-0 shrink-0"
            onClick={() => setColConfigOpen(true)}
            aria-label="Configure columns"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {visibleColCount < availableUtilCols.length && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {availableUtilCols.length - visibleColCount}
              </span>
            )}
          </Button>

          {/* Export to Excel */}
          {canExport && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0"
                  onClick={handleExportExcel}
                  aria-label="Export utilisation report to Excel"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export to Excel</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}
        </div>
      </div>

      {/* ── Column config sheet ───────────────────────────────────────────── */}
      <Sheet open={colConfigOpen} onOpenChange={setColConfigOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Configure columns</SheetTitle>
          <SheetDescription className="sr-only">Toggle columns on or off for the utilisation report table.</SheetDescription>
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold leading-tight">Customise Columns</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Toggle columns on or off to personalise the table.</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">{visibleColCount}</div>
                <span className="text-sm text-muted-foreground">of {availableUtilCols.length} columns showing</span>
              </div>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={resetUtilCols}>
                <RotateCcw className="h-3.5 w-3.5" />Reset defaults
              </Button>
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-2">
                {availableUtilCols.map((col) => {
                  const isVisible = visibleUtilCols.has(col.key);
                  return (
                    <div key={col.key} className={['flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150', isVisible ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30' : 'bg-muted/10 border-border/30'].join(' ')}>
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                      <div className="flex-1 min-w-0">
                        <p className={['text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground'].join(' ')}>{col.label}</p>
                        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">{col.description}</p>
                      </div>
                      <Switch checked={isVisible} onCheckedChange={() => toggleUtilCol(col.key)} aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label} column`} />
                    </div>
                  );
                })}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          <SheetFooter className="px-4 py-3 border-t shrink-0">
            <Button className="w-full" onClick={() => setColConfigOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Filter drawer ─────────────────────────────────────────────────── */}
      <Sheet open={filterDrawerOpen} onOpenChange={(open) => { if (!open) setFilterDrawerOpen(false); }}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Utilisation report filters</SheetTitle>
          <SheetDescription className="sr-only">{`Filter by resource, ${groupingColLabel.toLowerCase()} and role.`}</SheetDescription>
          {/* Header */}
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Filters</span>
              {(() => {
                const count = (stagedResources.length > 0 ? 1 : 0) + (stagedDepartments.length > 0 ? 1 : 0) + (stagedTeams.length > 0 ? 1 : 0) + (stagedRoles.length > 0 ? 1 : 0);
                return (
                  <Badge variant="secondary" className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10">
                    <Filter className="h-3 w-3" />
                    {count} {count === 1 ? 'filter' : 'filters'} active
                  </Badge>
                );
              })()}
            </div>
          </SheetHeader>
          {/* Body */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Resource</label>
                  <MultiSelect options={resourceOptions} placeholder="All resources" value={stagedResources} onValueChange={setStagedResources} maxDisplay={2} />
                </div>
                <Separator />
                {groupingFilter === 'team' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Planner Group</label>
                    <MultiSelect options={teamOptions} placeholder="All planner groups" value={stagedTeams} onValueChange={setStagedTeams} maxDisplay={2} />
                  </div>
                ) : groupingFilter === 'department' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Department</label>
                    <MultiSelect options={departmentOptions} placeholder="All departments" value={stagedDepartments} onValueChange={setStagedDepartments} maxDisplay={2} />
                  </div>
                ) : null}
                <Separator />
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Role</label>
                  <MultiSelect options={roleOptions} placeholder="All roles" value={stagedRoles} onValueChange={setStagedRoles} maxDisplay={2} />
                </div>
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          {/* Footer */}
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={clearAllFilters}
              disabled={(stagedResources.length + stagedDepartments.length + stagedTeams.length + stagedRoles.length) === 0}
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={applyFilters}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Scrollable content area ───────────────────────────────────────── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
        {isLiveDataLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading utilisation data…</p>
          </div>
        ) : liveBookingsError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <p className="text-sm font-medium text-foreground">Failed to load booking data</p>
            <p className="text-xs text-muted-foreground">{liveBookingsError}</p>
            <Button variant="outline" size="sm" onClick={() => fetchLiveBookings(dateFrom, dateTo)}>Retry</Button>
          </div>
        ) : data.isEmpty ? (
          <EmptyState onReset={handleReset} />
        ) : (
          <>
            {/* ── Stat cards ────────────────────────────────────────────── */}
            <div className="flex gap-3">
              <StatCard
                value={`${data.summary.avgUtilised}%`}
                accent="teal"
                label="Avg utilised"
                subLabel="Confirmed bookings · current period"
              />
              <StatCard
                value={`${data.summary.avgPlanned}%`}
                accent="warning"
                label="Avg planned utilisation"
                subLabel="Confirmed + planned bookings"
              />
              <StatCard
                value={`${data.summary.totalUtilisedHours}h`}
                label="Total confirmed hours"
                subLabel="Confirmed bookings only"
              />
              <StatCard
                value={`${data.summary.resourcesAtTarget} of ${data.summary.totalResources}`}
                label="Resources at target"
                subLabel="Meeting or exceeding utilisation target"
                isFilterable
                isActive={activeCardFilter === 'at-target'}
                onClick={() => {
                  setActiveCardFilter((prev) => prev === 'at-target' ? null : 'at-target');
                  setPage(1);
                }}
              />
            </div>

            {/* ── Active card filter pill ───────────────────────────────── */}
            {activeCardFilter && (
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-muted-foreground">Filtered by:</span>
                <button
                  type="button"
                  onClick={() => { setActiveCardFilter(null); setPage(1); }}
                  className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                >
                  {activeCardFilter === 'at-target' && 'Resources at target'}
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="shrink-0">
                    <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
                <span className="text-[11px] text-muted-foreground">
                  — showing {cardFilteredRows.length} of {data.rows.length} resources
                </span>
              </div>
            )}

            {/* ── Grouped bar chart ────────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)] p-4 flex flex-col gap-3"
              style={{
                background: 'var(--background)',
                border: '0.5px solid var(--color-border-tertiary, var(--border))',
              }}
            >
              {/* Chart toolbar */}
              <div className="flex items-center justify-between gap-3 flex-wrap shrink-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[12px] font-medium text-foreground">
                    {chartDisplayData.length} of {data.chartData.length} resources
                  </span>
                  {data.chartData.length > chartDisplayData.length && (
                    <span className="text-[11px] text-muted-foreground">
                      · filtered to top {chartDisplayData.length} · full data in table below
                    </span>
                  )}

                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {/* Sort by */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Sort by</span>
                    <Select
                      value={chartSortBy}
                      onValueChange={(v) =>
                        setChartSortBy(
                          v as 'name' | 'utilised-desc' | 'utilised-asc' | 'planned-desc' | 'variance-desc',
                        )
                      }
                    >
                      <SelectTrigger className="h-7 text-[12px] w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="utilised-desc">Utilised % (high → low)</SelectItem>
                        <SelectItem value="utilised-asc">Utilised % (low → high)</SelectItem>
                        <SelectItem value="planned-desc">Planned % (high → low)</SelectItem>
                        <SelectItem value="variance-desc">Variance (worst first)</SelectItem>
                        <SelectItem value="name">Name (A–Z)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Show limit */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Show</span>
                    <Select
                      value={String(chartShowLimit)}
                      onValueChange={(v) => setChartShowLimit(Number(v))}
                    >
                      <SelectTrigger className="h-7 text-[12px] w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="-1">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Chart area — responsive, fills full section width */}
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={chartDisplayData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                    barCategoryGap="35%"
                    barGap={2}
                  >
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      tickFormatter={(name: string) =>
                        name.length > 8 ? `${name.slice(0, 7)}…` : name
                      }
                    />
                    <YAxis
                      domain={[0, 120]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                    />
                    <RechartsTooltip
                      content={<ChartTooltip />}
                      cursor={{ fill: 'var(--accent)', opacity: 0.3 }}
                    />
                    <ReferenceLine
                      y={100}
                      stroke={GREY_DASHED()}
                      strokeDasharray="4 3"
                      strokeWidth={1.5}
                    />
                    <ReferenceLine
                      y={75}
                      stroke={TEAL()}
                      strokeDasharray="4 3"
                      strokeWidth={1.5}
                    />
                    <Bar dataKey="utilised" name="utilised" radius={[3, 3, 0, 0]} maxBarSize={22}>
                      {chartDisplayData.map((entry, idx) => (
                        <Cell key={idx} fill={barFillUtilised(entry)} />
                      ))}
                    </Bar>
                    <Bar
                      dataKey="planned"
                      name="planned"
                      radius={[3, 3, 0, 0]}
                      fill={barFillPlanned()}
                      maxBarSize={22}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 px-1">
                <LegendSwatch colour={TEAL()}        label="Utilised (confirmed)" />
                <LegendSwatch colour={settings.statusConfigs?.find(s => String(s.id) === 'planned')?.color || PLANNED_BLUE_FALLBACK()} label="Planned" />
                <LegendSwatch colour={TEAL()}        label="At target" />
                <LegendSwatch colour={AMBER()}       label="Below target" />
                <LegendSwatch colour={RED()}         label="Overloaded" />
                <LegendSwatch colour={TEAL()}        label="Target line"      dashed />
                <LegendSwatch colour={GREY_DASHED()} label="Capacity limit"   dashed />
              </div>
            </div>

            {/* ── Data table ───────────────────────────────────────────── */}
            <div
              className={RAW_TABLE_CONTAINER_CLASS}
            >
              <div className={RAW_TABLE_SCROLL_CLASS}>
                <table className={RAW_TABLE_CLASS}>
                  <thead>
                    <tr className={TABLE_HEADER_ROW_CLASS}>
                      <SortHeader label={viewBy === 'individual' ? 'Resource' : viewBy === 'team' ? 'Planner Group' : viewBy === 'department' ? 'Department' : 'Location'} col="name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={200} />
                      {showDepartmentCol && <SortHeader label={groupingColLabel} col="department" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                      {visibleUtilCols.has('target') && <SortHeader label="Target %"   col="target"     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleUtilCols.has('utilised') && <SortHeader label="Utilised %" col="utilised"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleUtilCols.has('planned') && <SortHeader label="Planned %"  col="planned"    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleUtilCols.has('variance') && <SortHeader label="Variance"   col="variance"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleUtilCols.has('confirmed') && <SortHeader label="Confirmed"  col="confirmed"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleUtilCols.has('plannedh') && <SortHeader label="Planned hrs" col="plannedh"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleUtilCols.has('available') && <SortHeader label="Available"  col="available"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                      {visibleUtilCols.has('status') && <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Status</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => (
                      <tr
                        key={row.id}
                        onClick={() => setDrawerRow(row)}
                        className="cursor-pointer transition-colors"
                        style={{
                          height: 48,
                          background:
                            alternatingRowBackground(idx),
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = TABLE_HOVER_BG;
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background =
                            alternatingRowBackground(idx);
                        }}
                      >
                        {/* Resource */}
                        <td className="px-3 py-2" style={{ minWidth: 200 }}>
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-7 w-7 shrink-0">
                              <AvatarImage src={row.photoUrl} alt={row.name} />
                              <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                                {row.initials}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-medium text-[13px] text-foreground truncate leading-tight">
                                {row.name}
                              </p>
                              <p className="text-[11px] text-muted-foreground truncate leading-tight">
                                {row.role}
                              </p>
                            </div>
                          </div>
                        </td>
                        {showDepartmentCol && <td className="px-3 py-2 text-foreground whitespace-nowrap">{row.department}</td>}
                        {visibleUtilCols.has('target') && <td className="px-3 py-2 text-right text-foreground">{row.targetPct}%</td>}
                        {visibleUtilCols.has('utilised') && <td className="px-3 py-2 text-right"><PctCell pct={row.utilisedPct} targetPct={row.targetPct} /></td>}
                        {visibleUtilCols.has('planned') && <td className="px-3 py-2 text-right"><PctCell pct={row.plannedPct} targetPct={row.targetPct} /></td>}
                        {visibleUtilCols.has('variance') && <td className="px-3 py-2 text-right"><VarianceCell variance={row.variancePct} /></td>}
                        {visibleUtilCols.has('confirmed') && <td className="px-3 py-2 text-right text-foreground">{row.utilisedHours}h</td>}
                        {visibleUtilCols.has('plannedh') && <td className="px-3 py-2 text-right text-muted-foreground">{row.plannedHours}h</td>}
                        {visibleUtilCols.has('available') && <td className="px-3 py-2 text-right text-foreground">{row.availableHours}h</td>}
                        {visibleUtilCols.has('status') && <td className="px-3 py-2"><StatusPill status={row.status} /></td>}
                      </tr>
                    ))}
                  </tbody>

                  {/* Footer totals row */}
                  {footerTotals && (
                    <tfoot>
                      <tr
                        style={{
                          background: 'var(--color-background-secondary, var(--muted))',
                          borderTop: '0.5px solid var(--color-border-primary, var(--border))',
                        }}
                      >
                        <td
                          className="px-3 py-2.5 font-medium text-[13px] text-foreground"
                          colSpan={showDepartmentCol ? 2 : 1}
                        >
                          {viewBy === 'individual' ? 'Total / average' : viewBy === 'team' ? 'Planner Group total / average' : viewBy === 'department' ? 'Department total / average' : 'Location total / average'}
                        </td>
                        {visibleUtilCols.has('target') && (
                        <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                          {footerTotals.targetAvg}%
                        </td>
                        )}
                        {visibleUtilCols.has('utilised') && (
                        <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                          <PctCell pct={footerTotals.utilisedAvg} targetPct={footerTotals.targetAvg} />
                        </td>
                        )}
                        {visibleUtilCols.has('planned') && (
                        <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                          <PctCell pct={footerTotals.plannedAvg} targetPct={footerTotals.targetAvg} />
                        </td>
                        )}
                        {visibleUtilCols.has('variance') && (
                        <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                          <VarianceCell variance={footerTotals.varianceAvg} />
                        </td>
                        )}
                        {visibleUtilCols.has('confirmed') && (
                        <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                          {footerTotals.confirmedSum}h
                        </td>
                        )}
                        {visibleUtilCols.has('plannedh') && (
                        <td className="px-3 py-2.5 text-right font-medium text-[13px] text-muted-foreground">
                          {footerTotals.plannedSum}h
                        </td>
                        )}
                        {visibleUtilCols.has('available') && (
                        <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                          {footerTotals.availableSum}h
                        </td>
                        )}
                        {visibleUtilCols.has('status') && <td />}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* ── Pagination ────────────────────────────────────────────── */}
            <div className="flex items-center justify-between shrink-0 pb-2">
              <p className="text-[13px] text-muted-foreground">
                Showing {Math.min((page - 1) * pageSize + 1, sortedRows.length)}–
                {Math.min(page * pageSize, sortedRows.length)} of {sortedRows.length} resources
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 text-[13px]"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <span className="text-[13px] text-muted-foreground px-1">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 text-[13px]"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[13px] text-muted-foreground">Rows per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
                >
                  <SelectTrigger className="h-8 w-20 text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Resource detail drawer ────────────────────────────────────────── */}
      <ResourceDrawer
        row={drawerRow}
        open={drawerRow !== null}
        onClose={() => setDrawerRow(null)}
        onNavigateToHeatmap={onNavigateToHeatmap}
      />
    </div>
  );
}
